import { customFetch } from '@agos/api-client'
import {
  isTicketOutstanding,
  type TicketCategory,
  type TicketPriority,
  type TicketStatus,
} from '@agos/shared'

/**
 * Tickets a franchise has raised with the platform - `FRONTEND.md` §9.513, `BACKEND.md` §10A.267.
 *
 * **`customFetch` rather than a generated hook, and that is §9.509's lesson.** The client is generated from a spec
 * exported from a running backend and no container starts here, so no hook exists for these paths yet. §9.509
 * recorded what treating that as a blocker costs: a whole feature was banked as *blocked* and the owner had to come
 * back and ask for it. These calls move to generated hooks after the reboot, which the freshness gate will prompt
 * for; `src/generated` is not touched.
 *
 * **Every response has a declared type rather than `unknown`.** §3.122's defect - a reader taking a key the server
 * does not send - read every franchise as active for a month, because `undefined !== false`. A declared type makes
 * the same mistake a typecheck failure.
 */

export interface TicketSummary {
  id: number
  subject: string
  category: TicketCategory
  priority: TicketPriority
  status: TicketStatus
  /** Null on a row raised before the column existed, which the server permits. */
  raised_by: string | null
  created_at: string
  updated_at: string
}

export interface TicketMessage {
  id: number
  body: string
  /** `franchise` or `platform`. The thread renders from this; §10A.267 says why it is not an id. */
  author_side: string
  author_name: string | null
  created_at: string
}

export interface TicketThread extends TicketSummary {
  messages: TicketMessage[]
}

export interface TicketDraft {
  subject: string
  body: string
  category: string
  priority: string
}

/** Every ticket this franchise has raised, newest first - the server orders it. */
export async function fetchTickets(): Promise<TicketSummary[]> {
  return customFetch<TicketSummary[]>('/api/v1/support-tickets')
}

export async function fetchThread(ticketId: number): Promise<TicketThread> {
  return customFetch<TicketThread>(`/api/v1/support-tickets/${String(ticketId)}`)
}

export async function raiseTicket(draft: TicketDraft): Promise<TicketThread> {
  return customFetch<TicketThread>('/api/v1/support-tickets', {
    method: 'POST',
    body: JSON.stringify(draft),
  })
}

/**
 * Add to the thread, and optionally move the ticket.
 *
 * **One call for both**, because a franchise closing a ticket almost always wants to say why - and the server
 * accepts the pair in one request precisely so *reply* and *close* are not two things that can half-happen.
 */
export async function replyToTicket(
  ticketId: number,
  body: string,
  status?: TicketStatus,
): Promise<TicketThread> {
  return customFetch<TicketThread>(`/api/v1/support-tickets/${String(ticketId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(status === undefined ? { body } : { body, status }),
  })
}

/**
 * How many tickets are still somebody's work, for the badge in the nav.
 *
 * `waiting_on_franchise` counts, which is the whole point of that status: a franchise that has been asked a
 * question and does not know it is exactly why the platform's queue would stall.
 */
export function outstandingCount(tickets: readonly TicketSummary[]): number {
  return tickets.filter((ticket) => isTicketOutstanding(ticket.status)).length
}

/**
 * The thread oldest first, which is how a conversation reads.
 *
 * The server already orders it; this is the screen not depending on that. A list that arrives in another order
 * would otherwise render a reply above the question it answers, and nothing would look broken.
 */
export function inOrder(messages: readonly TicketMessage[]): TicketMessage[] {
  return [...messages].sort((one, two) => one.created_at.localeCompare(two.created_at))
}

/** Whether this entry was written by the platform, for which side of the thread to draw it on. */
export function isFromPlatform(message: TicketMessage): boolean {
  return message.author_side === 'platform'
}
